import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, UserRound, Search, FileText, MessageSquare } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import SpotlightTour from "./SpotlightTour";

/**
 * WelcomeTour — spotlight-style guided tour for agents. Starts automatically
 * on first login; also can be launched manually from Settings via the
 * `openAgentTour()` helper below.
 *
 * The server-side `agents.welcome_tour_completed_at` flag is the source of
 * truth; localStorage is only used as an optimistic cache to prevent a
 * flicker on cold-start.
 */
const STORAGE_KEY = "ikey_tour_seen_v1";
const RESTART_KEY = "ikey_tour_restart";

// Exposed helper — Settings imports this to relaunch the tour on demand.
export function restartAgentTour() {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.setItem(RESTART_KEY, String(Date.now()));
  // Broadcast so a mounted WelcomeTour picks it up without a page reload.
  window.dispatchEvent(new CustomEvent("ikey:restart-tour"));
}

const STEPS = [
  {
    icon: Sparkles,
    eyebrow: "Welcome to iKEY",
    title: "You're on a 30-day free trial",
    body: "No credit card required. Every feature — leads, transactions, portals, e-signatures, briefings — is unlocked. Let's take a 60-second look at the parts you'll use most.",
    target: null,
    route: "/",
  },
  {
    icon: UserRound,
    eyebrow: "Step 2 · Profile",
    title: "Set up your profile & brand",
    body: "Tap your name in the top-left to open the account menu — your photo, brand color, and MLS search URL all live inside Profile & Settings. Clients see your headshot on every share link and every portal page.",
    // Point at the agent brandmark (the menu trigger) since Profile & Settings
    // lives inside the dropdown that isn't open yet.
    target: '[data-testid="brand-home"]',
    route: "/",
  },
  {
    icon: Sparkles,
    eyebrow: "Step 3 · AI briefings",
    title: "Your Run My Day briefing",
    body: "Every morning your top 5 leads bubble up here with a call-ready opener drafted by Gemini. Tap Refresh any time to re-rank based on the latest activity.",
    target: '[data-testid="nav-run-my-day"]',
    route: "/",
  },
  {
    icon: Search,
    eyebrow: "Step 4 · MLS Search",
    title: "Share listings in one click",
    body: "Paste any listing URL — we scrape the photo and title automatically. Recommend it to a lead or drop your MLS search URL so clients can browse the whole market from inside their portal.",
    target: '[data-testid="nav-mls-search"]',
    route: "/listings",
  },
  {
    icon: FileText,
    eyebrow: "Step 5 · Document Vault",
    title: "Store & send documents",
    body: "The Vault lives inside your account menu (top-left). Upload contracts, disclosures, and packets — send any file for DocuSign signature straight from a row, and signed copies auto-file back into the client's portal.",
    // Vault sits inside the profile dropdown — point at the trigger so the
    // agent knows where to click.
    target: '[data-testid="brand-home"]',
    route: "/",
  },
  {
    icon: MessageSquare,
    eyebrow: "Step 6 · Client messaging",
    title: "Chat with clients from anywhere",
    body: "Every transaction has a private thread with its client. Portal messages, deadline nudges, and DocuSign updates all funnel into Messages so nothing gets lost.",
    target: '[data-testid="nav-messages"]',
    route: "/messages",
  },
];

export default function WelcomeTour() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { agent } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // The onboarding wizard has its own choreography — never overlap.
    if (pathname.startsWith("/onboarding")) return;
    // Wait for the profile — server flag is the source of truth.
    if (!agent) return;
    // If a restart was explicitly requested, open regardless of flag.
    const restartAt = localStorage.getItem(RESTART_KEY);
    if (restartAt) {
      localStorage.removeItem(RESTART_KEY);
      setOpen(true);
      return;
    }
    if (agent.welcome_tour_completed_at) {
      localStorage.setItem(STORAGE_KEY, "1");
      return;
    }
    if (localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent]);

  // React to programmatic restart requests fired via the restartAgentTour helper.
  useEffect(() => {
    const onRestart = () => setOpen(true);
    window.addEventListener("ikey:restart-tour", onRestart);
    return () => window.removeEventListener("ikey:restart-tour", onRestart);
  }, []);

  const markComplete = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    api.post("/auth/me/welcome-tour-complete").catch(() => {});
  };

  // Finish → land on Profile & Settings so the agent can immediately act
  // on step 2 (upload photo, set brand color, drop MLS URL).
  const handleFinish = () => { markComplete(); setOpen(false); navigate("/settings"); };
  const handleSkip = () => { markComplete(); setOpen(false); };

  if (!open) return null;
  return (
    <SpotlightTour
      steps={STEPS}
      onFinish={handleFinish}
      onSkip={handleSkip}
      brandName="iKEY"
      testIdPrefix="welcome-tour"
    />
  );
}
